import axios from "axios";
import { WEBOT_SERVER_ENDPOINT } from "../logic/Consts";
import { firebaseApp } from "../firebaseConfig";
import { REST_schema } from "../@types/sharedSchema";

/**
 * Virtual-number settings API.
 *
 * The `payCall-settings/<number>` parent doc is not readable from the browser
 * (Firestore rules only open its `callers` / `calls-summary` subcollections), so
 * both reads and writes go through the server, which also enforces which fields a
 * customer may touch.
 *
 * Two surfaces consume this — the settings page section and the calls-page
 * settings dialog — and the calls dropdown wants the tags on every render, so
 * the list is cached module-side and revalidated in the background
 * (stale-while-revalidate), mirroring `services/signatures.ts`.
 */

const VIRTUAL_NUMBERS_API_URL = `${WEBOT_SERVER_ENDPOINT}/api/v1/virtual-numbers`;

export type VirtualNumberSettings = REST_schema.VirtualNumbers.VirtualNumberSettings;
export type UpdateVirtualNumberRequest = REST_schema.VirtualNumbers.UpdateVirtualNumberRequest;

const http = axios.create({ baseURL: VIRTUAL_NUMBERS_API_URL });

http.interceptors.request.use(async (config) => {
  try {
    const currentUser = firebaseApp.auth().currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      config.headers.Authorization = token;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error getting Firebase auth token for virtual numbers API:", error);
  }
  return config;
});

let virtualNumbersCache: VirtualNumberSettings[] | null = null;
let virtualNumbersInflight: Promise<VirtualNumberSettings[]> | null = null;

type CacheSubscriber = (virtualNumbers: VirtualNumberSettings[]) => void;
const subscribers = new Set<CacheSubscriber>();

const publish = (virtualNumbers: VirtualNumberSettings[]): void => {
  virtualNumbersCache = virtualNumbers;
  subscribers.forEach((subscriber) => subscriber(virtualNumbers));
};

/** Notifies on every cache change. Returns an unsubscribe function. */
export const subscribeToVirtualNumbers = (subscriber: CacheSubscriber): (() => void) => {
  subscribers.add(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
};

export const getCachedVirtualNumbers = (): VirtualNumberSettings[] | null => virtualNumbersCache;

/**
 * Fetches the user's manageable virtual numbers. Concurrent calls share one
 * request; pass `force` to bypass the cached value (still de-duplicated).
 */
export const fetchVirtualNumbers = async (force = false): Promise<VirtualNumberSettings[]> => {
  if (!force && virtualNumbersCache) return virtualNumbersCache;
  if (virtualNumbersInflight) return virtualNumbersInflight;

  virtualNumbersInflight = http
    .get<REST_schema.VirtualNumbers.ListVirtualNumbersResponse>("/")
    .then((response) => {
      const virtualNumbers = response.data?.virtualNumbers ?? [];
      publish(virtualNumbers);
      return virtualNumbers;
    })
    .finally(() => {
      virtualNumbersInflight = null;
    });

  return virtualNumbersInflight;
};

/**
 * Persists a partial update for one number and merges the server's echo back
 * into the cache, so the calls-page dropdown reflects a rename immediately.
 */
export const updateVirtualNumber = async (
  number: string,
  update: UpdateVirtualNumberRequest
): Promise<VirtualNumberSettings> => {
  const response = await http.patch<REST_schema.VirtualNumbers.UpdateVirtualNumberResponse>(
    `/${encodeURIComponent(number)}`,
    update
  );
  const updated = response.data.virtualNumber;

  const current = virtualNumbersCache ?? [];
  publish(current.map((vn) => (vn.number === updated.number ? updated : vn)));

  return updated;
};

/**
 * Formats an Israeli local number for display: `0527777777` → `052-777-7777`.
 * Virtual numbers are all 10-digit (05X / 07X); 9-digit landlines fall back to
 * `0X-XXX-XXXX`. Anything unexpected is returned untouched.
 */
export const formatLocalPhoneNumber = (raw?: string): string => {
  const digits = (raw ?? "").replace(/\D/g, "");
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
  return raw ?? "";
};

/** Lookup of number → tag, for labelling numbers outside the settings screens. */
export const buildTagsByNumber = (
  virtualNumbers: VirtualNumberSettings[] | null
): Record<string, string> => {
  const tags: Record<string, string> = {};
  (virtualNumbers ?? []).forEach((vn) => {
    if (vn.tag) tags[vn.number] = vn.tag;
  });
  return tags;
};
